import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { arrayOf, shape, string } from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import {
  Dropdown,
  Search,
  Tag,
} from '@groovehq/internal-design-system/lib/components'
import { button } from '@groovehq/internal-design-system/lib/styles/elements'
import { styles as dropdownStyles } from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown.styles'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'

// import { Plus } from '../../../assets/icons'
import { styles } from './styles'

const { Menu } = Dropdown

const menu = data => (
  <Menu>
    {data.map(({ key, label }) => (
      <Menu.Item key={key} itemKey={key}>
        {label}
      </Menu.Item>
    ))}
  </Menu>
)

const exists = (list, label) => list.some(i => i.label === label.trim())

const AddTag = ({ orderTags, existedTags }) => {
  const [searchValue, setSearchValue] = useState('')
  const searchRef = useRef(null)
  const [tags, setTags] = useState(existedTags)
  const [visible, setVisible] = useState(false)

  // Only show tags in Dropdown that haven't been choosen
  const availableTags = useMemo(
    () => {
      return orderTags.filter(t => {
        return !tags.find(tag => tag.key === t.key)
      })
    },
    [orderTags, tags]
  )
  const [filteredList, setFilteredList] = useState(availableTags)

  useEffect(
    () => {
      const lowercasedValue = searchValue.trim().toLowerCase()

      setFilteredList(
        availableTags.filter(
          ({ label }) => label.toLowerCase().indexOf(lowercasedValue) > -1
        )
      )
    },
    [availableTags, searchValue]
  )

  const handleChangeSeach = useCallback(({ target }) => {
    setSearchValue(target.value)
  }, [])

  const searchField = useMemo(
    () => (
      <div css={dropdownStyles.item}>
        <Search
          name="search"
          placeholder="Search for tags..."
          onChange={handleChangeSeach}
          value={searchValue}
          css={styles.search}
          ref={searchRef}
        />
      </div>
    ),
    [handleChangeSeach, searchValue]
  )

  const overlay = useMemo(
    () => {
      return (
        <>
          {searchField}
          {menu(filteredList)}
        </>
      )
    },
    [filteredList, searchField]
  )

  const handleVisibleChange = useCallback(isVisible => {
    setVisible(isVisible)
    if (!isVisible) {
      setSearchValue('')
    } else {
      searchRef.current.focus()
    }
  }, [])

  const handleSelect = useCallback(
    key => {
      const tag = orderTags.find(t => t.key === key)
      setTags([...tags, tag])
      handleVisibleChange(false)
    },
    [orderTags, tags, handleVisibleChange]
  )

  const showTags = useCallback(
    () => {
      if (tags.length === 0) {
        return 'Urgent, reviewed, wholesale'
      }
      return (
        <div css={styles.tags}>
          {tags.map(({ label, key }) => (
            <Tag key={key} closable>
              {label}
            </Tag>
          ))}
        </div>
      )
    },
    [tags]
  )

  const handleAddTag = useCallback(
    () => {
      handleVisibleChange(false)
      if (exists(tags, searchValue)) return
      const newTag = {
        key: uuidv4(),
        label: searchValue.trim(),
      }
      setTags([...tags, newTag])
    },
    [handleVisibleChange, searchValue, tags]
  )

  const footerBtns = useMemo(
    () => {
      if (searchValue.trim() === '' || filteredList.length > 0) return null
      return (
        <button
          type="button"
          css={[button.styles.link, styles.footerBtns]}
          onClick={handleAddTag}
        >
          <span>{`Add "${searchValue}"`}</span>
        </button>
      )
    },
    [filteredList.length, handleAddTag, searchValue]
  )

  return (
    <div css={styles.dropdownContainer}>
      <div css={fieldStyles.labelBox}>Tags</div>
      <Dropdown
        overlay={overlay}
        hasMinWidth
        css={styles.dropdown}
        onSelect={handleSelect}
        footer={footerBtns}
        visible={visible}
        onVisibleChange={handleVisibleChange}
        emptyHint="No tags found"
      >
        <button
          type="button"
          className="grui"
          css={[button.styles.preflight, styles.field]}
        >
          {showTags()}
        </button>
      </Dropdown>
    </div>
  )
}

AddTag.propTypes = {
  orderTags: arrayOf(
    shape({
      key: string,
      label: string,
    })
  ),
  existedTags: arrayOf(
    shape({
      key: string,
      label: string,
    })
  ),
}

AddTag.defaultProps = {
  orderTags: {},
  existedTags: {},
}

export default AddTag
